import React from 'react';
import { Button, Drawer, Spin } from 'antd';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ProspectTagVo } from 'types/prospectTag';
import s from './ProspectTagsDrawer.module.less';

type Props = {
    open: boolean;
    title: string;
    loading?: boolean;
    tags: ProspectTagVo[];
    onClose: () => void;
    onAddNewTags: () => void;
    onRemoveTag: (tagName: string) => void;
    onReorderTags: (nextTagNames: string[]) => void;
};

function CloseIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M6 18L18 6M6 6L18 18"
                stroke="#111827"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SortableTagBadge(
    props: Readonly<{
        id: string;
        onRemove: (tagName: string) => void;
    }>,
) {
    const { id, onRemove } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${s.tagBadge} ${isDragging ? s.tagBadgeDragging : ''}`}
            aria-label="Drag tag"
            {...attributes}
            {...listeners}
        >
            <span className={s.tagBadgeDrag} aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
                    <circle cx="3" cy="3" r="1" />
                    <circle cx="7" cy="3" r="1" />
                    <circle cx="3" cy="7" r="1" />
                    <circle cx="7" cy="7" r="1" />
                </svg>
            </span>
            <span className={s.tagBadgeLabel}>{id}</span>
            <button
                type="button"
                className={s.tagBadgeRemove}
                aria-label="Remove tag"
                onPointerDown={(e) => {
                    // Don't let DnD grab interactions start from the remove button.
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(id);
                }}
            >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                    <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
            </button>
        </div>
    );
}

export default function ProspectTagsDrawer(props: Readonly<Props>) {
    const { open, title, loading = false, tags, onClose, onAddNewTags, onRemoveTag, onReorderTags } = props;

    const tagNames = React.useMemo(() => {
        return (tags || [])
            .slice()
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((t) => t.tagName)
            .filter(Boolean);
    }, [tags]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = tagNames.indexOf(String(active.id));
            const newIndex = tagNames.indexOf(String(over.id));
            if (oldIndex < 0 || newIndex < 0) return;
            const next = arrayMove(tagNames, oldIndex, newIndex);
            onReorderTags(next);
        },
        [onReorderTags, tagNames],
    );

    return (
        <Drawer
            open={open}
            title={title}
            width={420}
            className={s.tagDrawer}
            onClose={onClose}
            closeIcon={<CloseIcon />}
        >
            {loading ? (
                <Spin />
            ) : (
                <div className={s.tagDrawerBody}>
                    <Button type="primary" className={s.tagAddBtn} onClick={onAddNewTags}>
                        Add new tags
                    </Button>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={tagNames} strategy={rectSortingStrategy}>
                            <div className={s.tagBadges}>
                                {tagNames.map((t) => (
                                    <SortableTagBadge key={t} id={t} onRemove={onRemoveTag} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            )}
        </Drawer>
    );
}
